'use client';
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useHash from '@/hooks/useHash';

import { blogContentId } from '@/cms/blogLayout.consts';
import Section from '@/components/atoms/Section/Section';
import {
  getSideNavigationElementsAsSections,
  normalizeHeadingForMatch,
  scrollEntryIntoView,
  scrollToHeadingElement,
  transformUrlHashToText,
  trasnformTextToUrlHash,
} from '@/features/blog/SideNavigation/utils';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { Text } from '@/components/atoms/Text';
import { hashPrefix } from './consts';
import SideNavigationEntry from './SideNavigationEntry';

export type SideNavigationProps = {
  title: string;
};

export function SideNavigationStrapi({ title }: SideNavigationProps) {
  const { hash } = useHash();

  const [stateHash, setHash] = useState(hash);

  const sideNavigationViewportRef = useRef<HTMLElement>(null);
  const lastReplacedHashRef = useRef<string>('');

  const [headingElements, setHeadingElements] = useState<HTMLHeadingElement[]>(
    []
  );

  // @Note: Avoid refactoring the navigation logic - there's risk of introducing
  // GTM collect requests firing on every scroll event.
  function navigateTo(text: string) {
    const hashUrl = trasnformTextToUrlHash(text);
    lastReplacedHashRef.current = normalizeHeadingForMatch(hashUrl);
    window.history.replaceState(null, '', hashUrl);
    handleHashChange(headingElements);
  }

  function getAndSetHeadingElements() {
    const mainContentElement = document.getElementById(blogContentId);
    if (!mainContentElement) return;

    const queriedHeadings = Array.from(
      mainContentElement.querySelectorAll('h2,h3')
    ) as HTMLHeadingElement[];

    /*
    a pretty expensive recalc. 
    Prevents the list from re-mounting and creating problems with scrolls
    */
    setHeadingElements((prev) => {
      const areReferencesTheSame =
        queriedHeadings.length === prev.length &&
        queriedHeadings.every((el, i) => el === prev[i]);

      return areReferencesTheSame ? prev : queriedHeadings;
    });
    return queriedHeadings;
  }

  const elementsAsSections = useMemo(
    () => getSideNavigationElementsAsSections(headingElements),

    [headingElements]
  );

  function handleScroll(headingElements: HTMLHeadingElement[]) {
    if (!headingElements.length) return;

    let targetElement: HTMLHeadingElement | null = null;

    // we want to find the nearest heading element to the top of the screen
    for (let i = 0; i < headingElements.length; i++) {
      const { top } = headingElements[i].getBoundingClientRect();
      const { innerHeight } = window;

      const percentageFromTop = 0.12;
      if (top > innerHeight * percentageFromTop) continue;

      targetElement = headingElements[i];
    }

    if (targetElement) {
      const text = trasnformTextToUrlHash(
        (targetElement as HTMLElement).innerText
      );
      const normalized = normalizeHeadingForMatch(text);
      if (normalized !== lastReplacedHashRef.current) {
        lastReplacedHashRef.current = normalized;
        setHash(normalized);
      }
    } else if (lastReplacedHashRef.current !== '') {
      lastReplacedHashRef.current = '';
      setHash('');
    }
  }

  useEffect(() => {
    const scrollHandler = () => {
      handleScroll(headingElements);
    };

    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [headingElements.length]);

  useEffect(() => {
    const tempHeadingElements = getAndSetHeadingElements();

    if (tempHeadingElements) {
      const tempHash = window.location.hash;

      if (tempHash) {
        handleScroll(tempHeadingElements);
        handleHashChange(tempHeadingElements);
      } else {
        window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      }
    }
  }, []);

  function handleHashChange(headingElementsInner?: HTMLHeadingElement[]) {
    const newHash = window.location.hash;

    if (newHash.includes(`${hashPrefix}-`)) {
      const text = transformUrlHashToText(newHash);

      scrollToHeadingElement(text, headingElementsInner || headingElements);
    }
  }

  function doesH2ContainActiveH3Child(id: string) {
    const h3subSectionsForH2Id = elementsAsSections.find(
      (el) => el.tag === 'H2' && el.id === id
    )?.children;

    if (!h3subSectionsForH2Id) return false;

    return h3subSectionsForH2Id.some((child) => isSelected(child.text));
  }

  function isSelected(text: string) {
    return (
      !!stateHash &&
      normalizeHeadingForMatch(transformUrlHashToText(stateHash)) ===
        normalizeHeadingForMatch(text)
    );
  }

  const activeSidebarEntryId = useMemo(() => {
    if (!stateHash) return null;

    for (const elementSection of elementsAsSections) {
      const activeChild = elementSection.children.find((child) =>
        isSelected(child.text)
      );
      if (activeChild) return activeChild.id;
      if (isSelected(elementSection.text)) return elementSection.id;
    }

    return null;
  }, [stateHash, elementsAsSections]);

  useLayoutEffect(() => {
    if (!activeSidebarEntryId) return;

    const scrollRoot = sideNavigationViewportRef.current;
    if (!scrollRoot) return;

    // XSS stuff
    const selectorId = CSS.escape(activeSidebarEntryId);

    const activeRow = scrollRoot.querySelector(`#${selectorId}`) as HTMLElement;

    if (!activeRow) return;

    scrollEntryIntoView(scrollRoot, activeRow, 120);
  }, [activeSidebarEntryId]);

  return (
    <>
      <Text
        variant='label-text-l'
        color='gray-500'
        classNames={{ wrapper: 'uppercase mb-3' }}
      >
        {title}
      </Text>
      <Section
        ref={sideNavigationViewportRef}
        className='min-h-0 overflow-y-auto [overflow-anchor:none]'
        data-tracker-section={SECTION_NAMES.SIDE_NAVIGATION}
      >
        <div
          className='flex flex-col pb-px'
          data-tracker-component='side-navigation'
        >
          {elementsAsSections.map((elementSection) => {
            const sectionText = elementSection.text;

            return (
              <Fragment key={elementSection.id}>
                <SideNavigationEntry
                  isSelected={
                    isSelected(sectionText) ||
                    doesH2ContainActiveH3Child(elementSection.id) ||
                    false
                  }
                  handleClick={() => navigateTo(sectionText)}
                  text={sectionText}
                  tag={'H2'}
                  id={elementSection.id}
                />

                {elementSection.children.map((childHeading, childIndex) => (
                  <SideNavigationEntry
                    handleClick={() => navigateTo(childHeading.text)}
                    isSelected={isSelected(childHeading.text) || false}
                    key={childHeading.id}
                    text={childHeading.text}
                    tag={'H3'}
                    id={childHeading.id}
                  />
                ))}
              </Fragment>
            );
          })}
        </div>
      </Section>
    </>
  );
}
